import {
  LayoutDashboard,
  Mail,
  Layers,
  FileText,
  Send,
  Ban,
  KeyRound,
  Cpu,
  Webhook,
  ScrollText,
  Settings2,
  LogOut,
  type LucideIcon,
} from "lucide-react";
import {
  Sidebar as LibSidebar,
  type SidebarNavGroup,
} from "@marekl/adminer/shell";
import type { Role, User } from "../../auth/queries";
import { LettermanBrand, LettermanStamp } from "../LettermanBrand";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

// Nothing here is role-gated: viewers see every page read-only; the
// admin-only users section lives inside Settings and gates itself.
const NAV_GROUPS: NavGroup[] = [
  {
    label: "Overview",
    items: [{ to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true }],
  },
  {
    label: "Mail",
    items: [
      { to: "/messages", label: "Messages", icon: Mail },
      { to: "/batches", label: "Batches", icon: Layers },
      { to: "/templates", label: "Templates", icon: FileText },
    ],
  },
  {
    label: "Delivery",
    items: [
      { to: "/senders", label: "Senders", icon: Send },
      { to: "/suppressions", label: "Suppressions", icon: Ban },
    ],
  },
  {
    label: "Integrations",
    items: [
      { to: "/api-keys", label: "API keys", icon: KeyRound },
      { to: "/workers", label: "Workers", icon: Cpu },
      { to: "/webhooks", label: "Webhooks", icon: Webhook },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/audit-log", label: "Audit log", icon: ScrollText },
      { to: "/settings", label: "Settings", icon: Settings2 },
    ],
  },
];

const ROLE_LABEL: Record<Role, string> = {
  viewer: "Viewer",
  admin: "Administrator",
};

interface SidebarProps {
  user: User;
  onLogout: () => void;
}

export function Sidebar({ user, onLogout }: SidebarProps) {
  // The label rides in a <span> so the theme can frame it like the card in a
  // pigeonhole's label holder, with the shelf rule running out of it.
  const groups: SidebarNavGroup[] = NAV_GROUPS.map((g) => ({
    label: <span>{g.label}</span>,
    items: g.items.map(({ to, label, icon: Icon, end }) => ({
      to,
      label,
      end,
      icon: <Icon aria-hidden="true" />,
    })),
  }));

  return (
    <LibSidebar
      groups={groups}
      brand={<LettermanBrand />}
      // Collapsed to the rail, the wordmark would be three cut-off letters;
      // the stamp is the half of the lockup that reads at 64px.
      brandMark={<LettermanStamp />}
      user={{
        displayName: user.username,
        roleLabel: ROLE_LABEL[user.role],
      }}
      onLogout={onLogout}
      logoutIcon={<LogOut aria-hidden="true" />}
    />
  );
}
